import type { InfoDialogItem, InfoDialogSection } from "../../components/molecules/InfoDialog/InfoDialog.types";
import type { StatusBannerTone } from "../../components/molecules/StatusBanner/StatusBanner.types";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import { elementTypeLabels, modelStatusCopy as copy, modelReviewCopy as reviewCopy } from "./modelStatus.copy";
import type { AspectChangeApi, ChangedAspectsApi, ModelChangeApi, ModelChangesApi } from "./modelReview.types";
import type { ModelStatusApi } from "./modelStatus.types";

/** What a person reads about the data model: one line, a second one, and how it looks. */
export interface ModelStatusNotice {
  title: string;
  detail: string | null;
  tone: StatusBannerTone;
  /** Building or getting the data ready for a build: the notice shows a spinner. */
  isBuilding: boolean;
  /** No data model yet: the notice links to the data sources. */
  linksToSources: boolean;
}

const withReason = (reason: string | null, bare: string, because: (reason: string) => string): string =>
  reason ? because(reason) : bare;

/** The words for each state but `ready`, which needs none. */
const toTitle = (status: ModelStatusApi): string | null => {
  switch (status.state) {
    case "preparing":
      return withReason(status.reason, copy.preparing, copy.preparingBecause);
    case "building":
      return status.started_at ? copy.buildingSince(formatRelativeTime(status.started_at)) : copy.building;
    case "none":
      return withReason(status.reason, copy.none, copy.noneBecause);
    case "held":
      return withReason(status.reason, copy.held, copy.heldBecause);
    case "failed":
      return withReason(status.reason, copy.failed, copy.failedBecause);
    default:
      return null;
  }
};

const TONES: Record<ModelStatusApi["state"], StatusBannerTone> = {
  preparing: "default",
  building: "default",
  none: "neutral",
  held: "attention",
  failed: "error",
  ready: "default",
};

/**
 * The knowledge-graph pages' banner: every state but `ready`. With a release served already, chat
 * still answers from it, and the banner says so.
 */
export const toModelStatusBanner = (status: ModelStatusApi | null | undefined): ModelStatusNotice | null => {
  const title = status ? toTitle(status) : null;
  if (!status || !title) return null;
  const isBuilding = status.state === "building" || status.state === "preparing";
  const firstBuild = isBuilding && !status.release;
  return {
    title,
    detail: status.release ? copy.previousAnswers : firstBuild ? copy.fillsIn : null,
    tone: TONES[status.state],
    isBuilding,
    linksToSources: status.state === "none",
  };
};

/** The create-Story note: only while there is no data model to answer from. It never blocks creating. */
export const toModelStatusNote = (status: ModelStatusApi | null | undefined): ModelStatusNotice | null => {
  const banner = toModelStatusBanner(status);
  if (!banner || status?.release) return null;
  return { ...banner, detail: copy.storyNote };
};

/** The held build the signed-in admin may review, or null. */
export const toReviewableRunId = (status: ModelStatusApi | null | undefined, canReview: boolean): string | null =>
  canReview && status?.state === "held" ? (status.held_run_id ?? null) : null;

/** What the review dialog shows: an intro, one section per kind of change, and what stops answering. */
export interface ModelReview {
  sections: InfoDialogSection[];
  /** The retired elements' names, for the Reject confirm. */
  stopsAnswering: string[];
}

const ASPECTS: (keyof ChangedAspectsApi)[] = ["name", "description", "definition", "source"];

const typeLabel = (elementType: string): string =>
  elementTypeLabels[elementType] ?? elementType.replace(/_/g, " ").replace(/^./, (first) => first.toUpperCase());

const changeName = (change: ModelChangeApi): string =>
  reviewCopy.label(change.name ?? reviewCopy.unnamed, typeLabel(change.element_type));

const isMultiline = (...texts: (string | null | undefined)[]): boolean => texts.some((text) => text?.includes("\n"));

/** One aspect in words: the text an added or retired element has, or old → new for a changed one. The label already names an added or retired one. */
const aspectLine = (label: string, aspect: AspectChangeApi, showBoth: boolean): string | null => {
  const before = aspect.old ?? null;
  const after = aspect.new ?? null;
  if (!showBoth) {
    const text = after ?? before;
    return text ? reviewCopy.oneLine(label, text) : null;
  }
  if (isMultiline(before, after)) {
    return [reviewCopy.before(label, before ?? reviewCopy.nothing), reviewCopy.after(label, after ?? reviewCopy.nothing)].join("\n");
  }
  return reviewCopy.oldToNew(label, before ?? reviewCopy.nothing, after ?? reviewCopy.nothing);
};

const toItem = (change: ModelChangeApi, showBoth: boolean): InfoDialogItem => {
  const lines = ASPECTS.flatMap((key) => {
    const aspect = key === "name" && !showBoth ? null : change.aspects[key];
    const line = aspect ? aspectLine(reviewCopy.aspect[key], aspect, showBoth) : null;
    return line ? [line] : [];
  });
  return {
    id: change.element_id,
    label: changeName(change),
    text: [change.note, ...lines, ...(change.stops_answering ? [reviewCopy.stopsAnswering] : [])].join("\n"),
  };
};

const toShare = (share: ModelChangesApi["change_share"]): string | null => {
  const value = share === null ? NaN : Number(share);
  return Number.isFinite(value) ? `${Math.round(value * 100)}%` : null;
};

/** A held build's changes in plain words: Added, Changed with old → new, Retired. */
export const toModelReview = (changes: ModelChangesApi): ModelReview => {
  const counts = reviewCopy.counts(changes.added.length, changes.changed.length, changes.retired.length);
  const share = toShare(changes.change_share);
  const groups: [string, string, ModelChangeApi[], boolean][] = [
    ["added", reviewCopy.added(changes.added.length), changes.added, false],
    ["changed", reviewCopy.changed(changes.changed.length), changes.changed, true],
    ["retired", reviewCopy.retired(changes.retired.length), changes.retired, false],
  ];
  return {
    sections: [
      { id: "about", heading: reviewCopy.aboutHeading, text: share ? reviewCopy.aboutShare(share, counts) : reviewCopy.about(counts) },
      ...groups.map(([id, heading, rows, showBoth]) => ({ id, heading, items: rows.map((row) => toItem(row, showBoth)) })),
    ],
    stopsAnswering: Array.from(new Set(changes.retired.filter((row) => row.stops_answering).map(changeName))),
  };
};
